import {Locale} from '@aboutyou/config';
import {SERVER_AUTH_TOKEN_HEADER} from '@aboutyou/user';
import type {Request} from 'express';
import {CACHEABLE_CRITICAL_CSS_PLACEHOLDER} from './reactServerMiddlewareStyles';
import {getAbTestGroupsFromHeaders} from './utils/getAbTestGroups';
import {getCurrentPageInfo} from './utils/getCurrentPageInfo';
import {CookieConfig, parseRequestCookies} from './utils/parseRequestCookies';

export const parseRequestProperties = ({
  req,
  cookieConfig,
}: {
  req: Request;
  cookieConfig?: CookieConfig;
}) => {
  const currentPageInfo = getCurrentPageInfo(req);

  const serverAuthToken = req.header(SERVER_AUTH_TOKEN_HEADER);

  const traceId = req.header('x-pulse')!;

  // if the page is rendered through a service worker we expect that it's a real user and no bot, we can therefore activate a client mode that removed certain information from the page for faster load times.
  const enableRealUserMode =
    Boolean(req.header('Service-Worker-Navigation-Preload')) &&
    currentPageInfo.locale !== Locale.nl_NL; // => we want to render the page with all the information for real users and also for bots in the NL as an experiment for two months -> ASFE-11182

  /**
   * The `X-Original-Method` header is set by the Cloudflare Worker based on the original request method.
   */
  const requestMethod = req.header('X-Original-Method') || req.method;
  const sendHeaderOnly = requestMethod === 'HEAD';

  // if a bot is detected, we will not render serve static information in script tags to reduce the traffic which we have to pay for. In case a bot still opens the page in an interactive browser, they will fetch the data from by another network request so that the page is still working
  const isBotRequest =
    Boolean(req.header('X-UA-Device')?.includes('bot')) ||
    req.query.enableBotMode === '1';

  const enableBotMode = isBotRequest;

  /**
   * The `X-Cacheable-Placeholders` header is set by the Cloudflare Worker if we need to only render placeholders for some components instead of the full content.
   * Format is a comma-separated list of placeholder names. Example: `X-Cacheable-Placeholders: DESKTOP_HEADER,DESKTOP_FOOTER`
   */
  const placeholders = req.header('X-Cacheable-Placeholders')?.split(',') ?? [];

  const forceRenderPlaceholders = Boolean(req.header('X-Force-Placeholders'));

  const disableCriticalCssInlining = placeholders.includes(
    CACHEABLE_CRITICAL_CSS_PLACEHOLDER,
  );

  const abTestGroups = getAbTestGroupsFromHeaders(req);
  const serverCookies = parseRequestCookies(req, cookieConfig);

  const isAppCheckout = currentPageInfo.isAppCheckout;

  // can be used to disable tracking for e.g. performance testing
  const disableWebTracking =
    req.query.disableWebTracking === '1' || isAppCheckout;
  const disableDatadogBrowser =
    req.query.disableDatadogBrowser === '1' || isAppCheckout;

  const disableManifestFile = currentPageInfo.isAppCheckout;

  const isUsercentricsDisabled =
    String(req.query.version).includes('app_') || // on app we dont want to show any consent banner
    req.query.disableConsentBanner === '1' ||
    isAppCheckout ||
    isBotRequest;

  const forceLinaria = req.query.forceLinaria === '1';
  const isAppLinksSubdomainRequest = currentPageInfo.isAppLinksSubdomainRequest;

  return {
    ...currentPageInfo,
    traceId,
    enableRealUserMode,
    requestMethod,
    sendHeaderOnly,
    isBotRequest,
    enableBotMode,
    placeholders,
    abTestGroups,
    serverCookies,
    disableWebTracking,
    disableDatadogBrowser,
    disableManifestFile,
    isUsercentricsDisabled,
    forceLinaria,
    forceRenderPlaceholders,
    disableCriticalCssInlining,
    serverAuthToken,
    isAppLinksSubdomainRequest,
  };
};
